import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, FormControlLabel } from '@material-ui/core';
import { useFormikContext } from 'formik';
import { useQuery } from '@apollo/client';
import { affinityToStem } from '@ourbranch/policy-utils';

import useSession from 'core/hooks/use-session';
import { Card } from 'core/components/card';
import { Label } from 'core/components/label';
import { Select } from 'core/components/select';
import { Tooltip } from 'core/components/tooltip';
import { isSweepAccountsEnabled } from 'core/helpers/sweep-account';
import { GET_SWEEP_ACCOUNT } from 'core/components/sweep-account/sweep-account.queries';
import { buildSweepPaymentMethodOptions } from './sweep-bind-option.helpers';
import useStyles from './sweep-bind-option.styles';

/**
 * New-business bind option (#33155): lets an independent agent pay the initial (down) payment
 * from their agency's sweep account. Shown only when the agent's affinity stem matches the
 * offer's and their agency has a sweep account. The customer's own payment method (collected in
 * the billing section above) is still saved for future installments — sweep covers the down
 * payment only. Eligible agents with no sweep account see a link to set one up.
 */
const SweepBindOption = ({ offer }) => {
  const classes = useStyles();
  const { isInternalAgent, canManageSweepAccount, user } = useSession();
  const { values, setFieldValue } = useFormikContext();

  const userStem = user?.affinityCode ? affinityToStem(user.affinityCode).toUpperCase() : null;
  const offerAffinity = offer?.quote?.global?.affinity;
  const offerStem = offerAffinity ? affinityToStem(offerAffinity).toUpperCase() : null;
  // Same gating as the account-menu + /sweep-account route: brand/env flag on, an independent
  // agent, and the agent's affinity stem matching the offer's. (#33155)
  const eligible = isSweepAccountsEnabled() && !isInternalAgent && !!userStem && !!offerStem && userStem === offerStem;

  const { data, loading } = useQuery(GET_SWEEP_ACCOUNT, { skip: !eligible, fetchPolicy: 'no-cache' });
  const sweepAccount = data?.getSweepAccount || null;

  // Render nothing until the account query resolves — otherwise the "set up a sweep account"
  // hint briefly flashes before an existing account loads.
  if (!eligible || loading) {
    return null;
  }

  // Only someone who can actually set the account up gets the setup hint; a producer whose
  // agency has no sweep account sees nothing. (#32606)
  if (!sweepAccount) {
    if (!canManageSweepAccount) {
      return null;
    }
    return (
      <Card className={classes.container}>
        <Tooltip
          label="sweep account payments"
          text="Set up a sweep account in the account menu (top-right person icon)."
          className={classes.hint}
        />
      </Card>
    );
  }

  const onToggle = (event) => {
    const { checked } = event.target;
    setFieldValue('useSweepAccount', checked);
    if (checked && !values.sweepPaymentMethodId) {
      setFieldValue('sweepPaymentMethodId', sweepAccount.defaultPaymentMethodId || sweepAccount.paymentMethods[0]?.id);
    }
  };

  return (
    <Card className={classes.container}>
      <FormControlLabel
        control={<Checkbox checked={Boolean(values.useSweepAccount)} onChange={onToggle} color="primary" />}
        label="Pay the initial payment from your agency sweep account"
      />
      {values.useSweepAccount && (
        <div className={classes.methodRow}>
          <Label type="infoLabel">Sweep payment method</Label>
          <Select
            fullWidth
            mode="light"
            name="sweepPaymentMethodId"
            value={values.sweepPaymentMethodId || ''}
            options={buildSweepPaymentMethodOptions(sweepAccount.paymentMethods)}
            onChange={(_name, value) => setFieldValue('sweepPaymentMethodId', value)}
            data-cy="sweep-bind-payment-method"
          />
          <Label type="infoValue" className={classes.note}>
            The customer&apos;s own payment method above is still saved for future installments.
          </Label>
        </div>
      )}
    </Card>
  );
};

SweepBindOption.propTypes = {
  offer: PropTypes.object.isRequired
};

export default SweepBindOption;
